import { useTranslation } from "react-i18next"

import type { Lane } from "../schemas"

import { AlertOctagon, Bug, MessageSquareHeart } from "@/components/icons"
import { cn } from "@/lib/utils"

type LaneOption = {
  lane: Lane
  icon: typeof Bug
  titleKey: string
  titleFallback: string
  descriptionKey: string
  descriptionFallback: string
  /** Optional SLA / commitment line. Shown in italics below the description. */
  routingKey?: string
  routingFallback?: string
}

const OPTIONS: LaneOption[] = [
  {
    lane: "response_feedback",
    icon: MessageSquareHeart,
    titleKey: "feedback.lanes.responseFeedback.title",
    titleFallback: "Answer feedback",
    descriptionKey: "feedback.lanes.responseFeedback.description",
    descriptionFallback:
      "The answer wasn't quite right? Let us know — we use this to keep improving the assistant.",
  },
  {
    lane: "bug",
    icon: Bug,
    titleKey: "feedback.lanes.bug.title",
    titleFallback: "Bug report",
    descriptionKey: "feedback.lanes.bug.description",
    descriptionFallback:
      "Something doesn't work the way you expect? Tell us what happened and we'll take a look.",
  },
  {
    lane: "incident",
    icon: AlertOctagon,
    titleKey: "feedback.lanes.incident.title",
    titleFallback: "Incident",
    descriptionKey: "feedback.lanes.incident.description",
    descriptionFallback: "Something is broken for you or your team and is blocking work right now.",
    routingKey: "feedback.lanes.incident.routing",
    routingFallback: "You should expect a reply within 1 business day.",
  },
]

type LanePickerProps = {
  onSelect: (lane: Lane) => void
}

export function LanePicker({ onSelect }: LanePickerProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">
        {t("feedback.lanes.intro", "What kind of feedback do you have?")}
      </p>
      <div className="space-y-2">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon
          return (
            <button
              key={opt.lane}
              type="button"
              className={cn(
                "group hover:border-primary/50 hover:bg-accent/40 flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors",
                "focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:ring-2 focus-visible:outline-none"
              )}
              onClick={() => onSelect(opt.lane)}
            >
              <Icon className="text-muted-foreground group-hover:text-primary mt-0.5 size-5 shrink-0 transition-colors" />
              <div className="space-y-1">
                <p className="text-sm font-medium">{t(opt.titleKey, opt.titleFallback)}</p>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  {t(opt.descriptionKey, opt.descriptionFallback)}
                </p>
                {opt.routingKey && opt.routingFallback && (
                  <p className="text-muted-foreground text-xs italic">
                    {t(opt.routingKey, opt.routingFallback)}
                  </p>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
